import { SeoHead } from "@/components/common/SeoHead";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { AnimatedSection } from "@/components/common/AnimatedSection";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Ship, Anchor, Utensils, Music, Sun, Star, Check, ArrowRight } from "lucide-react";
import { useEffect } from "react";

const cruiseFeatures = [
  { icon: Ship, title: "Premium Ships", desc: "Partner with top cruise lines worldwide" },
  { icon: Utensils, title: "Gourmet Dining", desc: "World-class cuisine and dining experiences" },
  { icon: Music, title: "Entertainment", desc: "Shows, activities, and nightlife" },
  { icon: Sun, title: "Destinations", desc: "Exotic ports and shore excursions" },
];

const popularRegions = [
  {
    name: "Caribbean",
    ports: "Nassau, Cozumel, St. Thomas",
    desc: "Crystal clear waters and tropical paradise",
    image: "https://images.unsplash.com/photo-1548574505-5e239809ee19?w=600&q=80"
  },
  {
    name: "Mediterranean",
    ports: "Barcelona, Rome, Santorini",
    desc: "Historic cities and stunning coastlines",
    image: "https://images.unsplash.com/photo-1499678329028-101435549a4e?w=600&q=80"
  },
  {
    name: "Alaska",
    ports: "Juneau, Ketchikan, Glacier Bay",
    desc: "Majestic glaciers and wildlife",
    image: "https://images.unsplash.com/photo-1531176175280-3b00e4a0bf65?w=600&q=80"
  },
];

export default function CruisePackagesPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div id="main-content" className="min-h-screen bg-white" data-testid="cruise-packages-page">
      <SeoHead
        title="Cruise Booking Assistance"
        description="Get help choosing and booking cruises with Tripsyfy, including cruise timing, travel coordination, and first-time cruise support."
        canonical="https://tripsyfy.com/services/cruise-packages"
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-24 lg:pt-28 pb-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center max-w-3xl mx-auto">
              <p className="text-accent text-sm font-semibold tracking-wide uppercase mb-4">Our Services</p>
              <h1 className="heading-xl text-primary mb-6">Cruise Packages</h1>
              <p className="text-base lg:text-lg text-muted-foreground leading-relaxed">
                Set sail on unforgettable voyages with our curated cruise packages. From Caribbean beaches to Mediterranean charm, discover the world from the comfort of world-class ships.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {cruiseFeatures.map((feature, i) => (
                <div key={feature.title} className="bg-white p-6 rounded-lg border border-border text-center">
                  <div className="h-12 w-12 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
                    <feature.icon className="h-6 w-6 text-accent" />
                  </div>
                  <h3 className="font-bold text-primary mb-2">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.desc}</p>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Popular Cruise Regions */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center mb-12">
              <h2 className="heading-lg text-primary mb-4">Popular Cruise Destinations</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Explore the world's most sought-after cruise regions with exclusive packages and itineraries.
              </p>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-3 gap-8">
            {popularRegions.map((region, i) => (
              <AnimatedSection key={region.name} delay={i * 0.1}>
                <div className="bg-white border border-border rounded-lg overflow-hidden hover:border-accent/30 transition-colors group">
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={region.image} 
                      alt={region.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="font-bold text-primary mb-1">{region.name}</h3>
                    <p className="text-sm text-muted-foreground mb-3">{region.desc}</p>
                    <div className="flex items-center gap-2 text-xs text-accent">
                      <Anchor className="h-3.5 w-3.5" />
                      <span>{region.ports}</span>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>

          <AnimatedSection>
            <div className="text-center mt-10">
              <Link to="/cruises">
                <Button size="lg" className="gap-2">
                  View All Cruise Packages <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-16 bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="heading-lg text-primary mb-6">What's Included in Our Cruise Packages</h2>
                <ul className="space-y-3">
                  {[
                    "Accommodation in your chosen cabin category",
                    "All onboard meals and select beverages",
                    "Access to pools, gyms, and entertainment venues",
                    "Evening shows and live performances",
                    "Kids club and family activities",
                    "Port taxes and gratuities (select packages)"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check className="h-5 w-5 text-accent shrink-0 mt-0.5" />
                      <span className="text-sm text-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1599640842225-85d111c60e6b?w=800&q=80" 
                  alt="Cruise ship deck" 
                  className="rounded-lg w-full h-80 object-cover"
                />
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AnimatedSection>
            <h2 className="heading-lg text-primary-foreground mb-4">Ready to Set Sail?</h2>
            <p className="text-primary-foreground/70 mb-8 max-w-xl mx-auto">
              Let us help you find the perfect cruise for your next vacation. Get a personalized quote today.
            </p>
            <Link to="/contact">
              <Button variant="navySolid" size="xl" className="gap-2">
                Request a Quote <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </AnimatedSection>
        </div>
      </section>

      <Footer />
    </div>
  );
}
